import { useProjectValue } from "components/projects/ProjectDraft";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios, { apiUrlForPath } from "../utils/axiosConfig";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import { Spinner } from "react-bootstrap";
import { ChevronLeft, ChevronRight, Play, Edit, Save, Check } from "lucide-react";
import { toast } from "hooks/use-toast";
import { Layout } from "components/Layout";

interface AudioChunk {
  id: number;
  translation_id: number;
  chunk_number: number;
  text: string;
  prepared: boolean;
  file_path: string | null;
  error_message: string | null;
  voice_id: string | null;
  voice_name: string | null;
}

export function StepByStepVoiceoverPage({ embedded = false }: { embedded?: boolean }) {
  const Wrapper = embedded ? React.Fragment : Layout;
  const { translation_id } = useParams<{ translation_id: string }>();
  const navigate = useNavigate();

  const [chunkDrafts, setChunkDrafts] = useProjectValue<Record<string, string>>("chunkDrafts", {});
  const [chunks, setChunks] = useState<AudioChunk[]>([]);
  const [currentChunkIndex, setCurrentChunkIndex] = useProjectValue("chunkIndex", 0);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCombining, setIsCombining] = useState(false);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);

  useEffect(() => {
    if (translation_id) {
      fetchChunks();
      fetchMetadata();
    }
  }, [translation_id]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (jobStatus === "generating_chunks") {
      interval = setInterval(() => {
        fetchMetadata();
        fetchChunks();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [jobStatus, translation_id]);

  const fetchMetadata = async () => {
    try {
      const resp = await axios.get(`/v2/translation/${translation_id}`);
      setJobStatus(resp.data.job_status);
    } catch (e) {
      console.error("Error fetching metadata:", e);
    }
  };

  const fetchChunks = async () => {
    try {
      setLoading(true);
      const response = await axios.get<AudioChunk[]>(`/v2/voiceover/chunks/${translation_id}`);
      setChunks(response.data);
      const restored = response.data[Math.min(currentChunkIndex, Math.max(0, response.data.length - 1))];
      setEditedText(chunkDrafts[String(restored?.id)] ?? restored?.text ?? "");
      setIsEditing(chunkDrafts[String(restored?.id)] !== undefined);
      if (currentChunkIndex >= response.data.length) setCurrentChunkIndex(Math.max(0, response.data.length - 1));
    } catch (error) {
      console.error("Error fetching chunks:", error);
      toast({
        title: "Error",
        description: "Failed to load chunks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const currentChunk = chunks[currentChunkIndex];
  const allChunksGenerated = chunks.length > 0 && chunks.every(c => c.file_path);
  const hasMissingAudio = chunks.some(c => !c.file_path);
  const isAnyActionDisabled = jobStatus === "generating_chunks" || isGenerating || isCombining || isGeneratingAll;

  const handlePrevious = () => {
    if (currentChunkIndex > 0) {
      setCurrentChunkIndex(currentChunkIndex - 1);
      setEditedText(chunkDrafts[String(chunks[currentChunkIndex - 1].id)] ?? chunks[currentChunkIndex - 1].text);
      setIsEditing(chunkDrafts[String(chunks[currentChunkIndex - 1].id)] !== undefined);
    }
  };

  const handleNext = () => {
    if (currentChunkIndex < chunks.length - 1) {
      setCurrentChunkIndex(currentChunkIndex + 1);
      setEditedText(chunkDrafts[String(chunks[currentChunkIndex + 1].id)] ?? chunks[currentChunkIndex + 1].text);
      setIsEditing(chunkDrafts[String(chunks[currentChunkIndex + 1].id)] !== undefined);
    }
  };

  const handleNextUngenerated = () => {
    const nextIndex = chunks.findIndex((c, idx) => idx > currentChunkIndex && !c.file_path);
    if (nextIndex !== -1) {
      setCurrentChunkIndex(nextIndex);
      setEditedText(chunkDrafts[String(chunks[nextIndex].id)] ?? chunks[nextIndex].text);
      setIsEditing(chunkDrafts[String(chunks[nextIndex].id)] !== undefined);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!currentChunk) return;

    try {
      await axios.put(`/v2/voiceover/chunk/${currentChunk.id}`, {
        text: editedText,
      });

      // Update local state
      const updatedChunks = [...chunks];
      updatedChunks[currentChunkIndex] = {
        ...updatedChunks[currentChunkIndex],
        text: editedText,
        prepared: true,
      };
      setChunks(updatedChunks);
      setChunkDrafts(previous => { const next = {...previous}; delete next[String(currentChunk.id)]; return next; });
      setIsEditing(false);

      toast({
        title: "Success",
        description: "Chunk text updated",
      });
    } catch (error) {
      console.error("Error saving chunk:", error);
      toast({
        title: "Error",
        description: "Failed to save chunk",
        variant: "destructive",
      });
    }
  };

  const handleGenerate = async () => {
    if (!currentChunk) return;

    try {
      setIsGenerating(true);
      const res = await axios.post<{ task_id: string }>(`/v2/voiceover/chunk/${currentChunk.id}/generate`);
      const { task_id } = res.data;

      toast({
        title: "Success",
        description: "Audio generation started",
      });

      // Poll task status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get<{ status: string; result: any }>(`/task_status/${task_id}`);
          const { status } = statusRes.data;

          if (status === "SUCCESS") {
            clearInterval(pollInterval);

            // Fetch updated chunk data
            const response = await axios.get<AudioChunk>(`/v2/voiceover/chunk/${currentChunk.id}`);

            // Force refresh audio by appending timestamp
            const chunkData = response.data;
            if (chunkData.file_path) {
              const separator = chunkData.file_path.includes("?") ? "&" : "?";
              chunkData.file_path = `${chunkData.file_path}${separator}t=${Date.now()}`;
            }

            setChunks(prevChunks =>
              prevChunks.map(c => c.id === currentChunk.id ? chunkData : c)
            );
            setIsGenerating(false);

            toast({
              title: "Success",
              description: "Audio generated successfully",
            });
          } else if (status === "FAILURE" || status === "REVOKED") {
            clearInterval(pollInterval);
            setIsGenerating(false);
            toast({
              title: "Error",
              description: "Audio generation failed",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("Polling error:", error);
        }
      }, 2000);

      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isGenerating) {
          setIsGenerating(false);
        }
      }, 300000);

    } catch (error) {
      console.error("Error generating audio:", error);
      setIsGenerating(false);
      toast({
        title: "Error",
        description: "Failed to start audio generation",
        variant: "destructive",
      });
    }
  };

  const handleGenerateAll = async () => {
    try {
      setIsGeneratingAll(true);
      await axios.post(`/v2/voiceover/generate-all/${translation_id}`);
      setJobStatus("generating_chunks");
      toast({
        title: "Started",
        description: "Generation for all missing chunks started.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to start batch generation.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingAll(false);
    }
  };

  const handleCombine = async () => {
    try {
      setIsCombining(true);
      await axios.post(`/v2/voiceover/${translation_id}/combine`);

      toast({
        title: "Success",
        description: "Audio files combined successfully",
      });

      // Navigate back to tasks page
      navigate("/tasks");
    } catch (error) {
      console.error("Error combining audio:", error);
      setIsCombining(false);
      toast({
        title: "Error",
        description: "Failed to combine audio files",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <div className="flex justify-center items-center min-h-screen">
          <Spinner animation="border" />
        </div>
      </Wrapper>
    );
  }

  if (!currentChunk) {
    return (
      <Wrapper>
        <div className="container mx-auto py-10">
          <Card className="p-6">
            <p>No chunks found</p>
          </Card>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="container mx-auto py-10 space-y-6">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-700">Step-by-Step Voiceover</h1>
          <p className="text-gray-500 mt-2">
            Chunk {currentChunkIndex + 1} of {chunks.length}
          </p>
        </div>

        <Card className="p-6 space-y-6">
          {/* Text area */}
          <div className="space-y-4">
            <textarea
              value={isEditing ? editedText : currentChunk.text}
              onChange={(e) => {setEditedText(e.target.value);setChunkDrafts(previous => ({...previous, [String(currentChunk.id)]:e.target.value}));}}
              disabled={!isEditing}
              className="w-full text-lg leading-relaxed p-6 border rounded-lg resize-none shadow-sm min-h-[500px] focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Audio player */}
          {currentChunk.file_path && (
            <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
              <label className="text-sm font-medium text-gray-700">Audio Preview</label>
              <audio 
                controls 
                className="w-full h-10" 
                key={currentChunk.file_path}
                src={apiUrlForPath(currentChunk.file_path)}
              >
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          {/* Controls Row */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t">
            {/* Status Indicator & Voice Info */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {currentChunk.file_path ? (
                  <>
                    <div className="p-1 bg-green-100 rounded-full">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm font-medium text-green-700">Generated</span>
                  </>
                ) : (
                  <>
                    <span className="h-3 w-3 rounded-full bg-gray-300"></span>
                    <span className="text-sm font-medium text-gray-500">Not generated</span>
                  </>
                )}
              </div>
              {(currentChunk.voice_name || currentChunk.voice_id) && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400 font-mono">
                    Voice: {currentChunk.voice_name || currentChunk.voice_id}
                    {currentChunk.voice_name && currentChunk.voice_id && ` (${currentChunk.voice_id})`}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons Group */}
            <div className="flex items-center gap-3">
              <Button
                onClick={isEditing ? handleSave : handleEdit}
                variant={isEditing ? "default" : "outline"}
                disabled={isAnyActionDisabled}
                className={!isEditing ? "text-blue-600 border-blue-200 hover:bg-blue-50" : ""}
              >
                {isEditing ? (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Text
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Text
                  </>
                )}
              </Button>

              <Button
                onClick={handleGenerate}
                disabled={isAnyActionDisabled || isEditing}
                className="min-w-[160px]"
              >
                {isGenerating ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    {currentChunk.file_path ? "Regenerate Audio" : "Generate Audio"}
                  </>
                )}
              </Button>

              {hasMissingAudio && (
                <Button
                  onClick={handleGenerateAll}
                  disabled={isAnyActionDisabled || isEditing}
                  variant="outline"
                  className="border-orange-200 text-orange-600 hover:bg-orange-50"
                >
                  {jobStatus === "generating_chunks" ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-2" />
                      Generating All...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Generate All Missing
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Navigation Row */}
          <div className="flex items-center justify-between pt-6 border-t mt-4">
            <Button
              onClick={handlePrevious}
              disabled={currentChunkIndex === 0 || isAnyActionDisabled}
              variant="ghost"
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              Previous Chunk
            </Button>

            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {currentChunkIndex + 1} / {chunks.length}
              </span>

              {chunks.some((c, idx) => idx > currentChunkIndex && !c.file_path) && (
                <Button
                  onClick={handleNextUngenerated}
                  disabled={isAnyActionDisabled}
                  variant="secondary"
                  size="sm"
                  className="text-xs"
                >
                  Skip to Next Ungenerated
                </Button>
              )}
            </div>

            <Button
              onClick={handleNext}
              disabled={currentChunkIndex === chunks.length - 1 || isAnyActionDisabled}
              variant="ghost"
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              Next Chunk
              <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          </div>

          {/* Combine button */}
          {allChunksGenerated && !hasMissingAudio && (
            <div className="pt-6">
              <Button
                onClick={handleCombine}
                disabled={isAnyActionDisabled}
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
              >
                {isCombining ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Combining...
                  </>
                ) : (
                  "Finalize & Combine All Chunks"
                )}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </Wrapper>
  );
}
